import { ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Chapter, Reveal } from "@/components/motion";
import { CREDENTIALS, IMAGES } from "@/data/site";

const APPROACH = [
  "Appropriate worker selection",
  "Safety awareness",
  "Site requirements",
  "Training and competency",
  "Compliance documentation",
  "Communication",
  "Incident reporting and response",
  "Ongoing workforce monitoring",
  "Continuous improvement",
];

export default function SafetyCompliance() {
  return (
    <div data-testid="safety-page">
      <PageHero
        index="04"
        label="Safety & Compliance"
        titleLines={["Safety Is", { t: "Non-Negotiable.", c: "text-gold" }]}
        intro="Safety is fundamental to everything we do. Construction is a high-responsibility environment, and Formcore recognises the importance of maintaining robust safety processes across every placement."
        image={IMAGES.safety}
      />

      <section className="bg-white py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Chapter index="01" label="Our Approach" />
          <Reveal delay={0.1}>
            <h2 className="mt-8 max-w-3xl font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl">
              Robust Processes. <span className="text-gold">Understood Requirements.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/75">
              Our management team has extensive experience in construction safety and compliance and works closely
              with clients to understand site-specific requirements and expectations.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-3">
            {APPROACH.map((item, i) => (
              <Reveal key={item} delay={(i % 3) * 0.06} className="h-full">
                <div className="group flex h-full items-start gap-4 bg-white p-6 transition-colors duration-300 hover:bg-navy" data-testid={`safety-approach-${i}`}>
                  <span className="font-heading text-sm font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-semibold leading-relaxed text-navy transition-colors duration-300 group-hover:text-white">
                    {item}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-28 lg:py-32">
        <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <Chapter index="02" label="Shared Responsibility" dark />
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl">
                  Working Together <span className="text-gold">on Safety</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-white/70">
                  Safety is a shared responsibility. Formcore works collaboratively with clients to ensure our
                  personnel understand their responsibilities and the requirements of each workplace.
                </p>
                <p className="mt-5 text-base leading-relaxed text-white/70">
                  We aim to establish a culture where safety is not simply a compliance obligation, but an integral
                  part of professional performance.
                </p>
                <p className="mt-10 font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                  Safe people. Safe workplaces. <span className="text-gold">Better outcomes.</span>
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="relative">
              <div className="absolute -right-5 -top-5 hidden h-full w-full border-2 border-gold/40 md:block" />
              <img
                src={IMAGES.safety}
                alt="Safety discussion between supervisor and site worker"
                className="clip-frame relative h-[300px] w-full object-cover md:h-[440px]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-surface py-28 lg:py-32" data-testid="credentials-section">
        <div className="mx-auto max-w-7xl px-6">
          <Chapter index="03" label="Capability & Credentials" />
          <Reveal delay={0.1}>
            <h2 className="mt-8 max-w-2xl font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl">
              Evidence of <span className="text-gold">Capability</span>
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-charcoal/70">
              Formcore maintains the licensing, insurance and compliance documentation expected by Tier 1 and Tier 2
              contractors and procurement teams. Credential details, certificates of currency and safety documentation
              are provided during procurement and pre-qualification discussions.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CREDENTIALS.map((cred, i) => (
              <Reveal key={cred.title} delay={(i % 3) * 0.06} className="h-full">
                <div className="h-full border border-navy/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-navy" data-testid={`credential-card-${i}`}>
                  <span className="grid h-11 w-11 place-items-center bg-gold/10 text-gold">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading text-base font-bold text-navy">{cred.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-charcoal/65">{cred.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-xs italic text-charcoal/50">
              Placeholder credential categories shown — only credentials actually held by Formcore are displayed on the live site.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection title="Need a Safety-Focused Workforce Partner?" />
    </div>
  );
}
